"use client";

import * as React from "react";
import Image from "next/image";
import { AnimatePresence, motion } from "framer-motion";
import {
  ChevronDown,
  Download,
  LogIn,
  Menu,
  X,
  ArrowUpRight,
} from "lucide-react";
import logo from "@/assets/logos/alertai_icon_light.svg";
import { downloads, navLinks, site, solutionLinks } from "@/data/site";
import { cn } from "@/lib/cn";
import Button from "@/components/primitives/Button";

interface NavbarProps {
  /**
   * overlay: fixa sobre o conteúdo (home).
   * flow: sticky dentro do fluxo da página (páginas internas/legadas).
   */
  variant?: "overlay" | "flow";
}

export default function Navbar({ variant = "overlay" }: NavbarProps) {
  const [scrolled, setScrolled] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [downloadOpen, setDownloadOpen] = React.useState(false);
  const [solutionsOpen, setSolutionsOpen] = React.useState(false);
  const [active, setActive] = React.useState<string | null>(null);
  const menuRef = React.useRef<HTMLLIElement>(null);
  const solutionsMenuRef = React.useRef<HTMLLIElement>(null);
  const toggleRef = React.useRef<HTMLButtonElement>(null);

  // Estado de scroll (transparente → vidro com blur)
  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Link ativo conforme a seção visível (apenas onde as seções existem)
  React.useEffect(() => {
    const targets = navLinks
      .map((l) => document.getElementById(l.id))
      .filter((el): el is HTMLElement => !!el);
    if (!targets.length) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          const id = e.target.id;
          // ativa ao entrar na faixa central; limpa ao sair (ex.: voltar ao topo)
          setActive((cur) => (e.isIntersecting ? id : cur === id ? null : cur));
        });
      },
      { rootMargin: "-45% 0px -50% 0px" }
    );
    targets.forEach((t) => io.observe(t));
    return () => io.disconnect();
  }, []);

  // Fecha os dropdowns (Download / Soluções) ao clicar fora / Escape
  React.useEffect(() => {
    if (!downloadOpen && !solutionsOpen) return;
    const onDown = (e: MouseEvent) => {
      if (downloadOpen && !menuRef.current?.contains(e.target as Node)) {
        setDownloadOpen(false);
      }
      if (solutionsOpen && !solutionsMenuRef.current?.contains(e.target as Node)) {
        setSolutionsOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setDownloadOpen(false);
        setSolutionsOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [downloadOpen, solutionsOpen]);

  // Menu mobile: trava scroll + Escape
  React.useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        toggleRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const solid = scrolled || open || variant === "flow";

  return (
    <header
      className={cn(
        "inset-x-0 top-0 z-50 w-full",
        variant === "overlay" ? "fixed" : "sticky"
      )}
    >
      {/*
        Fundo (vidro) numa camada própria: backdrop-filter no <header> faria dele o bloco
        de contenção do menu mobile (position: fixed) e o menu ficaria com 64px de altura.
      */}
      <div
        aria-hidden="true"
        className={cn(
          "pointer-events-none absolute inset-0 -z-10 border-b transition-[background-color,border-color,backdrop-filter] duration-300",
          variant === "flow"
            ? "border-line/10 bg-ink/95 backdrop-blur-xl"
            : solid
              ? "border-line/10 bg-ink/70 backdrop-blur-xl supports-[not(backdrop-filter)]:bg-ink/95"
              : "border-transparent bg-transparent"
        )}
      />
      {/*
        Disposição estilo GitHub: logo + links agrupados à esquerda (Download é um menu com seta),
        ações à direita. No celular: menu à esquerda, logo ao centro e "Entrar" à direita.
      */}
      <nav
        aria-label="Principal"
        className="mx-auto grid h-[68px] w-full max-w-[1280px] grid-cols-[1fr_auto_1fr] items-center gap-4 px-5 sm:px-8 lg:flex"
      >
        <button
          ref={toggleRef}
          type="button"
          aria-label={open ? "Fechar menu" : "Abrir menu"}
          aria-expanded={open}
          aria-controls="mobile-menu"
          onClick={() => setOpen((v) => !v)}
          className="inline-flex h-10 w-10 items-center justify-center justify-self-start rounded-full border border-line/15 bg-white/[0.04] text-fg lg:hidden focus-visible:outline focus-visible:outline-2 focus-visible:outline-electric"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>

        <a href="/" aria-label="Alert AI — página inicial" className="shrink-0 rounded-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-electric">
          <Image
            src={logo}
            alt="Alert AI"
            priority
            className="h-[26px] w-auto sm:h-[30px]"
          />
        </a>

        {/* Links (desktop), ao lado do logo */}
        <ul className="hidden items-center gap-1 lg:ml-6 lg:flex">
          {navLinks.map((l) => {
            if (l.id === "tecnologia") {
              return (
                <li key={l.id} ref={solutionsMenuRef} className="relative">
                  <button
                    type="button"
                    aria-haspopup="menu"
                    aria-expanded={solutionsOpen}
                    aria-current={active === l.id ? "true" : undefined}
                    onClick={() => setSolutionsOpen((v) => !v)}
                    className={cn(
                      "group relative inline-flex h-9 items-center gap-1.5 rounded-full px-3.5 text-[15px] font-semibold transition-colors",
                      "focus-visible:outline focus-visible:outline-2 focus-visible:outline-electric",
                      active === l.id ? "text-fg" : "text-fg/80 hover:text-fg"
                    )}
                  >
                    {l.label}
                    <ChevronDown
                      className={cn(
                        "h-3.5 w-3.5 transition-transform",
                        solutionsOpen && "rotate-180"
                      )}
                      aria-hidden="true"
                    />
                    <span
                      aria-hidden="true"
                      className={cn(
                        "absolute inset-x-3.5 -bottom-0.5 h-px origin-left bg-gradient-to-r from-electric to-violet transition-transform duration-300",
                        active === l.id ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"
                      )}
                    />
                  </button>
                  <AnimatePresence>
                    {solutionsOpen && (
                      <motion.div
                        role="menu"
                        initial={{ opacity: 0, y: -6, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -6, scale: 0.98 }}
                        transition={{ duration: 0.16 }}
                        className="absolute left-0 top-[calc(100%+10px)] w-64 origin-top-left rounded-2xl border border-line/15 bg-surface/95 p-1.5 shadow-2xl shadow-black/50 backdrop-blur-xl"
                      >
                        <a
                          role="menuitem"
                          href={l.href}
                          onClick={() => setSolutionsOpen(false)}
                          className="flex items-center rounded-xl px-3 py-2.5 text-sm font-medium text-fg transition-colors hover:bg-white/[0.06] focus-visible:bg-white/[0.06] focus-visible:outline-none"
                        >
                          Visão geral
                        </a>
                        {solutionLinks.map((s) => (
                          <a
                            key={s.label}
                            role="menuitem"
                            href={s.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => setSolutionsOpen(false)}
                            className="flex items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-muted transition-colors hover:bg-white/[0.06] hover:text-fg focus-visible:bg-white/[0.06] focus-visible:text-fg focus-visible:outline-none"
                          >
                            {s.label}
                            <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
                          </a>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </li>
              );
            }
            return (
              <li key={l.id}>
                <a
                  href={l.href}
                  aria-current={active === l.id ? "true" : undefined}
                  className={cn(
                    "group relative inline-flex h-9 items-center rounded-full px-3.5 text-[15px] font-semibold transition-colors",
                    "focus-visible:outline focus-visible:outline-2 focus-visible:outline-electric",
                    active === l.id ? "text-fg" : "text-fg/80 hover:text-fg"
                  )}
                >
                  {l.label}
                  <span
                    aria-hidden="true"
                    className={cn(
                      "absolute inset-x-3.5 -bottom-0.5 h-px origin-left bg-gradient-to-r from-electric to-violet transition-transform duration-300",
                      active === l.id ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"
                    )}
                  />
                </a>
              </li>
            );
          })}

          <li ref={menuRef} className="relative">
            <button
              type="button"
              aria-haspopup="menu"
              aria-expanded={downloadOpen}
              onClick={() => setDownloadOpen((v) => !v)}
              className="inline-flex h-9 items-center gap-1.5 rounded-full px-3.5 text-[15px] font-semibold text-fg/80 transition-colors hover:text-fg focus-visible:outline focus-visible:outline-2 focus-visible:outline-electric"
            >
              Download
              <ChevronDown
                className={cn(
                  "h-3.5 w-3.5 transition-transform",
                  downloadOpen && "rotate-180"
                )}
                aria-hidden="true"
              />
            </button>
            <AnimatePresence>
              {downloadOpen && (
                <motion.div
                  role="menu"
                  initial={{ opacity: 0, y: -6, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -6, scale: 0.98 }}
                  transition={{ duration: 0.16 }}
                  className="absolute left-0 top-[calc(100%+10px)] w-72 origin-top-left rounded-2xl border border-line/15 bg-surface/95 p-1.5 shadow-2xl shadow-black/50 backdrop-blur-xl"
                >
                  {downloads.map((d) => (
                    <a
                      key={d.id}
                      role="menuitem"
                      href={d.href}
                      data-track="download"
                      data-track-label={d.id}
                      onClick={() => setDownloadOpen(false)}
                      className="group flex items-center gap-3 rounded-xl px-3 py-2.5 transition-colors hover:bg-white/[0.06] focus-visible:bg-white/[0.06] focus-visible:outline-none"
                    >
                      <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand/15 text-electric">
                        <Download className="h-4 w-4" aria-hidden="true" />
                      </span>
                      <span className="flex flex-col">
                        <span className="text-sm font-medium text-fg">{d.label}</span>
                        <span className="text-xs text-muted">{d.hint}</span>
                      </span>
                    </a>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </li>
        </ul>

        {/* Ações (direita) */}
        <div className="flex items-center gap-2 justify-self-end sm:gap-3 lg:ml-auto">
          <Button
            href={site.loginUrl}
            variant="secondary"
            size="sm"
            icon={<LogIn className="h-4 w-4" aria-hidden="true" />}
          >
            Entrar
          </Button>
          <span className="hidden lg:block">
            <Button
              href="/#contato"
              size="sm"
              data-track="contact_click"
              data-track-label="navbar"
            >
              Entrar em contato
            </Button>
          </span>
        </div>
      </nav>

      {/* Menu mobile */}
      <AnimatePresence>
        {open && (
          <motion.div
            id="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-x-0 bottom-0 top-[68px] overflow-y-auto bg-ink/95 px-5 pb-10 pt-6 backdrop-blur-2xl lg:hidden"
          >
            <ul className="flex flex-col">
              {navLinks.map((l, i) => (
                <motion.li
                  key={l.id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 + i * 0.05 }}
                  className="border-b border-line/10"
                >
                  <a
                    href={l.href}
                    onClick={() => setOpen(false)}
                    className="flex items-center justify-between py-4 text-2xl font-semibold tracking-tight text-fg"
                  >
                    {l.label}
                    <ArrowUpRight className="h-5 w-5 text-muted" aria-hidden="true" />
                  </a>
                  {l.id === "tecnologia" && (
                    <ul className="flex flex-col gap-1 pb-4">
                      {solutionLinks.map((s) => (
                        <li key={s.label}>
                          <a
                            href={s.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => setOpen(false)}
                            className="flex items-center justify-between rounded-xl px-1 py-2.5 text-base font-medium text-muted transition-colors hover:text-fg"
                          >
                            {s.label}
                            <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
                          </a>
                        </li>
                      ))}
                    </ul>
                  )}
                </motion.li>
              ))}
            </ul>

            <div className="mt-8 grid gap-3">
              <p className="text-xs font-medium uppercase tracking-[0.14em] text-muted">
                Download
              </p>
              {downloads.map((d) => (
                <a
                  key={d.id}
                  href={d.href}
                  data-track="download"
                  data-track-label={d.id}
                  className="flex items-center gap-3 rounded-2xl border border-line/15 bg-white/[0.03] p-3.5"
                >
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand/15 text-electric">
                    <Download className="h-4 w-4" aria-hidden="true" />
                  </span>
                  <span className="flex flex-col">
                    <span className="text-sm font-medium text-fg">{d.label}</span>
                    <span className="text-xs text-muted">{d.hint}</span>
                  </span>
                </a>
              ))}
            </div>

            <div className="mt-8 flex flex-col gap-3">
              <Button
                href="/#contato"
                size="lg"
                fullWidth
                data-track="contact_click"
                data-track-label="navbar-mobile"
                onClick={() => setOpen(false)}
              >
                Entrar em contato
              </Button>
              <Button
                href={site.loginUrl}
                variant="secondary"
                size="lg"
                fullWidth
                icon={<LogIn className="h-4 w-4" aria-hidden="true" />}
              >
                Entrar na plataforma
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
